import { css } from "emotion";
import { Component } from "preact";
import { connect } from "react-redux";
import { debounce } from "throttle-debounce";
import Button from "../../components/global/Button";
import Loading from "../../components/global/Loading";
import NoResults from "../../components/global/NoResults";
import Layout from "../../components/layout/Layout";
import Card from "../../components/search/card/Card";
import SearchServices from "../../components/search/searchServices";
import { getEvents } from "../../state/actions/searchActions";
import { firstOfType, lastOfType, max } from "../../utils/CssUtils";
import { camelize, updateTitle, normalizeName } from "../../utils/BrowserUtils";
import { route } from "preact-router";
import queryString from "query-string";

const nonStandardRegionImage = require("../../assets/locations/placeholder.jpg");
const noImage = require("../../assets/locations/no_image.svg");
const maginifyingGlass = require("../../assets/ic_magnifying_glass.svg");

const bannerImages = {
  sydney: require("../../assets/locations/sydney.jpg"),
  brisbane: require("../../assets/locations/brisbane.jpg"),
  canberra: require("../../assets/locations/canberra.jpg"),
  "gold coast": require("../../assets/locations/gold_coast.jpg"),
  melbourne: require("../../assets/locations/melbourne.jpg"),
  perth: require("../../assets/locations/perth.jpg"),
  auckland: require("../../assets/locations/auckland.jpg"),
  wellington: require("../../assets/locations/wellington.jpg"),
  tauranga: require("../../assets/locations/tauranga.jpg")
};
const paragraphTexts = {
  Today:
    "Discover the best events today, incredible things to do and every activity you could wish for. Get outdoors, watch a show, discover something delicious, or be inspired.",
  "This Weekend":
    "If you’re looking for activities and things to do today, Friday, Saturday or Sunday, you’ll find it here. From date ideas to kids activities, discover all the best things to do.",
  "This Week":
    "Plan ahead with this week's best activities and events. Find tickets to incredible things to do, performances, workshops and more."
};

//styles
const pageWrapper = css({
  maxWidth: 1040,
  margin: "0 auto",
  paddingTop: 32,
  [max(1068)]: {
    paddingLeft: 14,
    paddingRight: 14
  }
});

const bannerWrapper = css({
  position: "relative",
  minHeight: 400,
  marginBottom: 44,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  h1: {
    paddingBottom: 19,
    marginBottom: 16,
    span: {
      [firstOfType()]: {
        display: "block",
        fontSize: 16,
        fontWeight: 400
      },
      [lastOfType()]: {
        fontSize: 36,
        fontWeight: 600,
        paddingBottom: 16,
        borderBottom: "3px solid #6621c0"
      }
    }
  },
  h2: {
    maxWidth: 412,
    fontSize: 16
  },
  p: {
    maxWidth: 412,
    fontSize: 16,
    marginBottom: 40
  },
  [max(768)]: {
    minHeight: 0,
    h1: {
      span: {
        [firstOfType()]: {
          fontSize: 14
        },
        [lastOfType()]: {
          fontSize: 22,
          paddingBottom: 12
        }
      },
      marginBottom: 12
    },
    h2: {
      fontSize: 14
    },
    p: {
      fontSize: 14,
      marginBottom: 20
    }
  }
});

const timeRefinementWrapper = css({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  maxWidth: 360,
  a: {
    fontSize: 16,
    fontWeight: 600,
    padding: "9px 0px"
    // borderRight: "1px solid #6621c0",
  },
  span: {
    height: 40,
    width: 1,
    backgroundColor: "#6621c0"
  },
  [max(1068)]: {
    marginBottom: 40
  },
  [max(768)]: {
    a: {
      fontSize: 14
    },
    span: {
      height: 32
    },
    marginBottom: 20
  }
});

const bannerImg = css({
  position: "absolute",
  width: 720,
  height: 400,
  top: 0,
  left: 460
});

const resultsWrapper = css({
  maxWidth: 1040,
  margin: "0 auto",
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "space-between",
  [max(1000)]: {
    maxWidth: 685
  },
  [max(750)]: {
    maxWidth: 400,
    display: "block"
  }
});

const cardWrapper = css({
  marginBottom: 24
});

const searchWrapper = css({
  padding: "12px 0px",
  backgroundColor: "#f9f9fa",
  marginBottom: 40,
  [max(768)]: {
    marginBottom: 0
  }
});

const searchCentring = css({
  maxWidth: 1040,
  margin: "0 auto",
  input: {
    maxWidth: 300,
    width: "100%",
    height: 40,
    borderRadius: 4,
    border: "1px solid #8d949e",
    fontSize: 16,
    paddingLeft: 34,
    paddingTop: 4,
    backgroundImage: `url(${maginifyingGlass})`,
    backgroundRepeat: "no-repeat",
    backgroundPositionX: 12,
    backgroundPositionY: 11
  },
  [max(1068)]: {
    paddingLeft: 14,
    paddingRight: 14
  }
});

const searchResultsWrapper = css({
  h3: {
    fontSize: 26,
    fontWeight: 600,
    marginBottom: 24
  },
  [max(768)]: {
    h3: {
      marginBottom: 12,
      fontSize: 20
    }
  }
});

const additionalSearchInformation = css({
  display: "flex",
  justifyContent: "center",
  width: "100%"
});

//banner
const Banner = ({ displayLocation, imgUrl, baseUrl, dateRefinementObj }) => {
  if (!displayLocation && !dateRefinementObj) {
    return (
      <div className={pageWrapper}>
        <h1>
          <span />
          <span />
        </h1>
        <p />
        <div className={bannerWrapper}>
          <img src={noImage} className={bannerImg} alt="placeholder image" />
        </div>
      </div>
    );
  }

  if (!displayLocation) {
    return (
      <div className={pageWrapper}>
        <div className={bannerWrapper}>
          <h1>
            <span />
            <span>
              What's on{" "}
              {dateRefinementObj &&
                dateRefinementObj.dateRefinement.toLowerCase()}
            </span>
          </h1>
          <p>
            {dateRefinementObj
              ? paragraphTexts[dateRefinementObj.dateRefinement]
              : ""}
          </p>
          <img src={imgUrl} className={bannerImg} alt="People partying" />
        </div>
      </div>
    );
  }

  const bannerImage =
    bannerImages[displayLocation.toLowerCase()] || nonStandardRegionImage;

  return (
    <div className={pageWrapper}>
      <div className={bannerWrapper}>
        <h1>
          <span>
            Things to do{" "}
            {dateRefinementObj
              ? `${dateRefinementObj.dateRefinement.toLowerCase()}`
              : "in"}
          </span>
          <span>{displayLocation}</span>
        </h1>
        <h2>
          Wondering what to do in {displayLocation}? Discover {displayLocation}
          ’s best activities, free events and tickets.
        </h2>
        <p>
          Find what’s on today, this weekend or all year. Search for everything
          from yoga to festivals, from gigs to sports, from stuff for kids to
          adults-only. All the best events are here.
        </p>
        <div className={timeRefinementWrapper}>
          <a
            href={`${baseUrl}`}
            style={{ color: !dateRefinementObj ? "#6621c0" : "#323232" }}
            alt={`All events in ${displayLocation}`}
          >
            All
          </a>
          <span />
          <a
            href={`${baseUrl}/today`}
            style={{
              color:
                dateRefinementObj &&
                dateRefinementObj.dateRefinement === "Today"
                  ? "#6621c0"
                  : "#323232"
            }}
            alt={`Events today in ${displayLocation}`}
          >
            Today
          </a>
          <span />
          <a
            href={`${baseUrl}/weekend`}
            style={{
              color:
                dateRefinementObj &&
                dateRefinementObj.dateRefinement === "This Weekend"
                  ? "#6621c0"
                  : "#323232"
            }}
            alt={`Events this weekend in ${displayLocation}`}
          >
            This Weekend
          </a>
          <span />
          <a
            href={`${baseUrl}/week`}
            style={{
              color:
                dateRefinementObj &&
                dateRefinementObj.dateRefinement === "This Week"
                  ? "#6621c0"
                  : "#323232"
            }}
            alt={`Events this week in ${displayLocation}`}
          >
            This Week
          </a>
        </div>
        <img
          src={bannerImage}
          className={bannerImg}
          alt={`${displayLocation} landscape`}
        />
      </div>
    </div>
  );
};

class SEOSearchPage extends Component {
  initialiseSearch = () => {
    const { country, location, getEvents, time, content, url } = this.props;
    const search = typeof window !== "undefined" ? window.location.search : "";
    const parsed = queryString.parse(search);
    const dateRefinementObj = this.generateTimeBounds(time);
    getEvents(
      parsed.query,
      content.locale,
      country,
      location,
      dateRefinementObj
    );
    const baseUrl = this.generateBaseUrl(url.split("?")[0]);
    let titleString = [];
    if (location) {
      titleString.push(location.charAt(0).toUpperCase() + location.slice(1));
    }
    if (dateRefinementObj) {
      titleString.push(dateRefinementObj.dateRefinement);
    }
    updateTitle(titleString.join(" | "));
    this.setState({
      country,
      location,
      time,
      dateRefinementObj,
      baseUrl,
      searchTerm: parsed.query
    });
  };

  generateTimeBounds = time => {
    let dateRefinement;
    switch (time) {
      case "today":
        dateRefinement = "today";
        break;
      case "weekend":
        dateRefinement = "thisWeekend";
        break;
      case "week":
        dateRefinement = "thisWeek";
        break;
    }
    let dateRefinementObj = SearchServices.getDateRefinement(
      dateRefinement,
      new Date()
    );
    return dateRefinementObj;
  };

  searchAlgolia = e => {
    const { url } = this.props;
    route(`${url.split("?")[0]}?query=${encodeURI(e.target.value)}`);
  };

  generateBaseUrl = url => {
    const urlComponents = url.split("/");
    switch (urlComponents[urlComponents.length - 1]) {
      case "today":
        return url.replace("/today", "");
      case "week":
        return url.replace("/week", "");
      case "weekend":
        return url.replace("/weekend", "");
      default:
        return url;
    }
  };
  constructor(props) {
    super(props);
    this.state = {
      imgUrl: nonStandardRegionImage,
      searchTerm: null
    };
  }

  componentDidMount = () => {
    this.initialiseSearch();
  };

  componentDidUpdate(prevProps) {
    if (this.props.url !== prevProps.url) {
      this.initialiseSearch();
    }
  }
  render() {
    const {
      imgUrl,
      location,
      searchTerm,
      country,
      baseUrl,
      dateRefinementObj
    } = this.state;
    const { search, content } = this.props;
    const displayLocation = location ? normalizeName(location) : "";
    const displayCountry = country ? normalizeName(country) : "";
    const events = search.locationEvents;
    let resultsTitle;
    if (displayLocation && displayCountry) {
      resultsTitle = `Events ${
        events && events.in ? "in" : "near"
      } ${displayLocation}, ${displayCountry}`;
      if (dateRefinementObj) {
        resultsTitle = `Events ${dateRefinementObj.dateRefinement.toLowerCase()} ${
          events && events.in ? "in" : "near"
        } ${displayLocation}, ${displayCountry}`;
      }
    } else {
      resultsTitle = `Events ${dateRefinementObj &&
        dateRefinementObj.dateRefinement.toLowerCase()}`;
    }
    return (
      <Layout>
        <Banner
          displayLocation={displayLocation}
          imgUrl={imgUrl}
          baseUrl={baseUrl}
          dateRefinementObj={dateRefinementObj}
        />
        <div className={searchWrapper}>
          <div className={searchCentring}>
            <input
              type="text"
              onKeyUp={debounce(750, false, e => this.searchAlgolia(e))}
              value={searchTerm}
              placeholder="Search Events"
              aria-label="Search Events"
            />
          </div>
        </div>

        <div className={pageWrapper}>
          <div className={searchResultsWrapper}>
            <h3>{resultsTitle}</h3>
            {search.loading || !events ? (
              <div className={additionalSearchInformation}>
                <Loading />
              </div>
            ) : (
              <div className={resultsWrapper}>
                {events.hits.length > 0 ? (
                  events.hits.map(hit => (
                    <div className={cardWrapper}>
                      <Card {...hit} />
                    </div>
                  ))
                ) : (
                  <div className={additionalSearchInformation}>
                    <NoResults
                      text="No results found"
                      subText="Sorry, we weren't able to find any results that matched"
                    />
                  </div>
                )}
              </div>
            )}
            {search.loading ||
            !search.locationEvents ||
            !search.locationEvents.locationInformation ||
            (events && events.hits && events.hits.length < 12) ? null : (
              <Button
                style="hollow"
                size="large"
                link={`/${content.locale}/search?&${
                  displayLocation
                    ? `location=${encodeURI(
                        `${displayLocation}, ${displayCountry}`
                      )}&lat=${
                        search.locationEvents.locationInformation.lat
                      }&lng=${search.locationEvents.locationInformation.lng}`
                    : ""
                }${searchTerm ? `&term=${encodeURI(searchTerm)}` : ""}${
                  dateRefinementObj
                    ? `&timeframe=${camelize(dateRefinementObj.dateRefinement)}`
                    : ""
                }`}
              >
                See More
              </Button>
            )}
          </div>
        </div>
        <div style={{ height: 72 }} />
      </Layout>
    );
  }
}

export default connect(
  (state, ownProps) => ({
    search: state.search,
    content: state.content
  }),
  {
    getEvents
  }
)(SEOSearchPage);
